/**
 * Consent-gated Mixpanel for the FacetFlux app.
 *
 * Mixpanel is initialised (and tracks) ONLY when the `ff_consent` cookie
 * mirrored from www grants analytics. No cookie = no tracking (opt-in).
 * The choice is re-checked on every load and whenever the tab regains focus
 * (the visitor may change "Cookie settings" on www in another tab):
 *  - granted            → init (once) and opt in if previously opted out
 *  - revoked / missing  → if Mixpanel had been used in this browser, init
 *                         without page-view tracking and opt_out_tracking(),
 *                         which also clears its persisted state; otherwise
 *                         Mixpanel is never initialised at all.
 *
 * Use `track()` from this module instead of `mixpanel.track` so calls are a
 * safe no-op without consent.
 */
import mixpanel from "mixpanel-browser";
import { hasAnalyticsConsent } from "./consent";

const token = import.meta.env.VITE_MIXPANEL_TRACKING_KEY;
const devMode = import.meta.env.VITE_IS_DEBUG === "true";

let initialised = false;
let enabled = false;

function baseConfig() {
  return devMode
    ? { debug: true, ignore_dnt: false, persistence: "localStorage" }
    : { debug: false, ignore_dnt: true, persistence: "localStorage" };
}

function hasMixpanelState() {
  try {
    const storage = window.localStorage;
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i) || "";
      if (key.startsWith("mp_") || key.startsWith("__mp_opt_in_out_")) return true;
    }
  } catch {
    /* ignore */
  }
  return /(?:^|;\s*)mp_/.test(document.cookie || "");
}

function ensureInit(config) {
  if (initialised) return;
  mixpanel.init(token, { ...baseConfig(), ...config });
  initialised = true;
}

/** Apply the current consent state. Safe to call repeatedly. */
export function applyAnalyticsConsent() {
  if (!token) {
    enabled = false;
    return false;
  }
  const granted = hasAnalyticsConsent();
  try {
    if (granted) {
      const firstInit = !initialised;
      ensureInit({ track_pageview: true });
      if (mixpanel.has_opted_out_tracking?.()) {
        mixpanel.opt_in_tracking();
      }
      if (!firstInit && !enabled) mixpanel.track_pageview?.();
      enabled = true;
      if (devMode) console.warn("Mixpanel: analytics consent granted, tracking (dev mode)");
    } else {
      if (initialised || hasMixpanelState()) {
        ensureInit({ track_pageview: false, opt_out_tracking_by_default: true });
        mixpanel.opt_out_tracking();
      }
      enabled = false;
      if (devMode) console.debug("Mixpanel: no analytics consent, not tracking");
    }
  } catch (e) {
    enabled = false;
    console.warn("Mixpanel consent handling failed", e);
  }
  return enabled;
}

/** Initialise on load and re-check when the tab regains focus. */
export function initAnalytics() {
  applyAnalyticsConsent();
  const recheck = () => {
    if (document.visibilityState === "visible") applyAnalyticsConsent();
  };
  document.addEventListener("visibilitychange", recheck);
  window.addEventListener("focus", recheck);
}

export const isAnalyticsEnabled = () => enabled;

/** Consent-safe mixpanel.track: no-op unless analytics consent was granted. */
export function track(event, properties) {
  if (!enabled) return;
  try {
    mixpanel.track(event, properties);
  } catch (e) {
    console.warn("mixpanel.track failed", e);
  }
}
